import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { CREATE_CART_LENS_PRODUCT_VARIANTS } from '../../../../server';
import { Cart } from '@hiwaldo/sdk/interfaces';
import { cartSlice, StoreState } from '../../../redux';
import { useCreateCartEffect } from './create-cart.effect';
import { useWindow } from '../../../../effects';
import { modernVersionBridge } from '../../../utils';
import { useState } from 'react';

export interface CreateCartLensProductVariantsArgs {
  leftEye?: {
    productVariantId: number;
    quantity: number;
  };
  rightEye?: {
    productVariantId: number;
    quantity: number;
  };
  bothEyes?: {
    productVariantId: number;
    quantity: number;
  };
}

type CreateCartLensProductVariantsEffectReturn = {
  createCartLensProductVariants: (
    args: CreateCartLensProductVariantsArgs,
    forceNew?: boolean
  ) => void;
  loading: boolean;
  success: boolean;
  error: string | undefined;
};

export const useCreateCartLensProductVariantsEffect = (
  callback?: () => void
): CreateCartLensProductVariantsEffectReturn => {
  const dispatch = useDispatch();
  const { cart, basketDataState } = useSelector((state: StoreState) => ({
    cart: state.orders.cart.cart,
    basketDataState: state.data.basket,
  }));

  const { region } = useWindow();
  const { createCart } = useCreateCartEffect();

  const [cartLensProductVariantsData, setCartLensProductVariantsData] =
    useState<CreateCartLensProductVariantsArgs | undefined>(undefined);

  const [doCreateCartLensProductVariants, createCartLensProductVariantsState] =
    useMutation(CREATE_CART_LENS_PRODUCT_VARIANTS, {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        dispatch(
          cartSlice.actions.updateCart(data.createCartLensProductVariants)
        );
        modernVersionBridge.basket.updateCart(
          dispatch,
          basketDataState,
          data.createCartLensProductVariants as Cart
        );
        if (callback) {
          callback();
        }
      },
      onError: (e) => {
        if (e.message.includes('Payment has already been taken for cart')) {
          if (cartLensProductVariantsData) {
            createCartLensProductVariants(cartLensProductVariantsData, true);
          }
        }
      },
    });

  const { loading, called, error } = createCartLensProductVariantsState;

  const createCartLensProductVariants = (
    args: CreateCartLensProductVariantsArgs,
    forceNew = false
  ): void => {
    if (args.leftEye || args.rightEye || args.bothEyes) {
      modernVersionBridge.basket.loading(dispatch, basketDataState, true);

      if (cart && !forceNew) {
        const leftEye = args.leftEye
          ? { ...args.leftEye, cartId: cart.id }
          : undefined;

        const rightEye = args.rightEye
          ? { ...args.rightEye, cartId: cart.id }
          : undefined;

        const bothEyes = args.bothEyes
          ? { ...args.bothEyes, cartId: cart.id }
          : undefined;

        const data = { leftEye, rightEye, bothEyes };

        setCartLensProductVariantsData(args);
        doCreateCartLensProductVariants({ variables: { data } }).then();
      } else {
        const props = { ...args, region: region! };
        createCart(
          (props, cartId) => {
            const leftEye = props.leftEye
              ? { ...props.leftEye, cartId }
              : undefined;

            const rightEye = props.rightEye
              ? { ...props.rightEye, cartId }
              : undefined;

            const bothEyes = args.bothEyes
              ? { ...args.bothEyes, cartId }
              : undefined;

            const data = { leftEye, rightEye, bothEyes };

            doCreateCartLensProductVariants({ variables: { data } }).then();
          },
          props,
          undefined,
          forceNew
        );
      }
    }
  };

  const success = !loading && called && !error;

  return {
    createCartLensProductVariants,
    loading,
    success,
    error: error?.message,
  };
};
